import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { ZynxIcon, type ZynxIconName, type ZynxTone } from '@/shared/components/ui/ZynxIcon';

/**
 * The summary tile at the top of list screens ("Total Quotations 3").
 */
export type StatTone = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const STAT_TO_ZYNX_TONE: Record<StatTone, ZynxTone> = {
  primary: 'indigo',
  info: 'sky',
  success: 'emerald',
  warning: 'amber',
  danger: 'rose',
  neutral: 'indigo',
};

const TONE: Record<StatTone, { chip: string; bar: string }> = {
  primary: { chip: 'bg-primary/10 text-primary ring-primary/15', bar: 'bg-primary' },
  info: { chip: 'bg-info/10 text-info-text ring-info/20', bar: 'bg-info' },
  success: { chip: 'bg-success/10 text-success-text ring-success/20', bar: 'bg-success' },
  warning: { chip: 'bg-warning/10 text-warning-text ring-warning/20', bar: 'bg-warning' },
  danger: { chip: 'bg-danger/10 text-danger-text ring-danger/20', bar: 'bg-danger' },
  neutral: { chip: 'bg-surface-variant text-on-surface-variant ring-outline', bar: 'bg-on-surface-subtle' },
};

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Short context under the value — "Awaiting client response". */
  hint?: ReactNode;
  icon?: LucideIcon;
  zynxIcon?: ZynxIconName;
  zynxTone?: ZynxTone;
  tone?: StatTone;
  /** 0–100. Only pass it when it is a real share of something. */
  progress?: number;
  /** Makes the whole tile a button (e.g. apply the matching filter). */
  onClick?: () => void;
  /** Highlights the tile as the active filter. */
  active?: boolean;
  loading?: boolean;
  className?: string;
}

export function StatCard({
  label, value, hint, icon: Icon, zynxIcon, zynxTone, tone = 'primary', progress, onClick, active = false, loading = false, className,
}: StatCardProps) {
  const t = TONE[tone];
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      data-stat-tone={tone}
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'rbm-metric group flex w-full flex-col rounded-card border border-l-4 bg-surface px-5 py-4 text-left transition-colors duration-150 ease-standard',
        active ? 'border-primary/40 ring-2 ring-primary/15' : 'border-outline',
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-label-caps uppercase text-on-surface-variant">{label}</p>
          {loading ? (
            <span className="mt-2 block h-7 w-20 animate-pulse rounded-md bg-surface-variant" />
          ) : (
            <p className="mt-1.5 truncate font-display text-[24px] font-bold leading-tight tabular-nums text-on-surface">
              {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
            </p>
          )}
        </div>
        {(Icon || zynxIcon) && (
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-200',
              t.chip,
              'group-hover:scale-105',
            )}
          >
            <ZynxIcon
              name={zynxIcon}
              icon={Icon}
              tone={zynxTone || STAT_TO_ZYNX_TONE[tone]}
              variant="bare"
              size={20}
            />
          </div>
        )}
      </div>
      {hint && <p className="mt-1 truncate text-body-sm text-on-surface-variant">{hint}</p>}
      {typeof progress === 'number' && Number.isFinite(progress) && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-variant" aria-hidden>
          <div className={cn('h-full rounded-full', t.bar)} style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
      )}
    </Wrapper>
  );
}

/** Responsive grid for StatCards — 1 column on phones, up to `cols` on desktop. */
export function StatGrid({ children, cols = 4, className }: { children: ReactNode; cols?: 2 | 3 | 4 | 5; className?: string }) {
  const lg = { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' }[cols];
  return <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', lg, className)}>{children}</div>;
}
